import { Controller } from "@hotwired/stimulus"

/**
 * Admin Command Palette Controller (Cmd+K)
 *
 * Features:
 * - Global keyboard shortcut (Cmd/Ctrl+K)
 * - Debounced AJAX search (300ms)
 * - Keyboard navigation (↑/↓/Enter/Esc)
 * - Recent searches (localStorage)
 * - Empty states and loading indicators
 */
export default class extends Controller {
  static targets = [
    "backdrop",
    "dialog",
    "input",
    "results",
    "loading",
    "empty",
    "noResults",
    "searchResults",
    "usersGroup",
    "usersList",
    "productsGroup",
    "productsList",
    "listingsGroup",
    "listingsList",
    "reportsGroup",
    "reportsList",
    "recommendationLogsGroup",
    "recommendationLogsList",
    "actionsGroup",
    "actionsList",
    "recentContainer",
    "recentList",
    "item"
  ]

  static values = {
    searchUrl: String
  }

  connect() {
    // Global keyboard shortcut (Cmd/Ctrl+K)
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleGlobalKeydown)

    // Debounce timer
    this.searchTimeout = null

    // Selected item index
    this.selectedIndex = -1

    // Window event listener for cross-controller command palette open
    // (topbar button dispatches via admin-sidebar relay since admin-command is a sibling)
    this._boundOpenFromWindow = this.open.bind(this)
    window.addEventListener("admin-command:open", this._boundOpenFromWindow)

    // Load recent searches
    this.loadRecentSearches()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleGlobalKeydown)
    window.removeEventListener("admin-command:open", this._boundOpenFromWindow)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // Global keyboard shortcut handler
  handleGlobalKeydown(event) {
    // Cmd+K or Ctrl+K
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
  }

  // Open command palette
  open() {
    this.element.classList.remove("hidden")

    // Trigger animations
    requestAnimationFrame(() => {
      this.backdropTarget.classList.remove("opacity-0")
      this.dialogTarget.classList.remove("opacity-0", "scale-95")
    })

    // Focus input
    this.inputTarget.focus()

    // Reset state
    this.inputTarget.value = ""
    this.selectedIndex = -1
    this.showEmpty()
  }

  // Close command palette
  close() {
    // Animate out
    this.backdropTarget.classList.add("opacity-0")
    this.dialogTarget.classList.add("opacity-0", "scale-95")

    // Hide after animation
    setTimeout(() => {
      this.element.classList.add("hidden")
    }, 200)
  }

  // Handle input changes (debounced search)
  handleInput(event) {
    const query = event.target.value.trim()

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Empty query - show empty state
    if (query === "") {
      this.showEmpty()
      return
    }

    // Show loading
    this.showLoading()

    // Debounced search (300ms)
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  // Perform AJAX search
  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        throw new Error("Search request failed")
      }

      const data = await response.json()
      this.renderResults(data, query)

      // Save to recent searches
      this.saveRecentSearch(query)
    } catch (error) {
      console.error("Search error:", error)
      this.showNoResults()
    }
  }

  // Render search results
  renderResults(data, query) {
    const { users, products, listings, reports, recommendation_logs, actions } = data
    const listingsData = listings || []
    const recLogs = recommendation_logs || []

    // Check if we have any results
    const hasResults = users.length > 0 || products.length > 0 || listingsData.length > 0 || reports.length > 0 || recLogs.length > 0 || actions.length > 0

    if (!hasResults) {
      this.showNoResults()
      return
    }

    // Hide other states
    this.hideAllStates()
    this.searchResultsTarget.classList.remove("hidden")

    // Render each group
    this.renderGroup(this.usersGroupTarget, this.usersListTarget, users, "user")
    this.renderGroup(this.productsGroupTarget, this.productsListTarget, products, "product")
    this.renderGroup(this.listingsGroupTarget, this.listingsListTarget, listingsData, "listing")
    this.renderGroup(this.reportsGroupTarget, this.reportsListTarget, reports, "report")
    this.renderGroup(this.recommendationLogsGroupTarget, this.recommendationLogsListTarget, recLogs, "recommendation_log")
    this.renderGroup(this.actionsGroupTarget, this.actionsListTarget, actions, "action")

    // Reset selection
    this.selectedIndex = -1
  }

  // Render a result group
  renderGroup(groupTarget, listTarget, items, type) {
    if (items.length === 0) {
      groupTarget.classList.add("hidden")
      return
    }

    groupTarget.classList.remove("hidden")
    listTarget.innerHTML = items.map((item, index) => this.renderItem(item, type, index)).join("")
  }

  // Render a single item
  renderItem(item, type, index) {
    const icons = {
      user: "user",
      product: "package",
      listing: "building-2",
      report: "alert-triangle",
      recommendation_log: "sparkles",
      action: "arrow-right"
    }

    return `
      <a
        href="${item.path}"
        data-admin-command-target="item"
        data-action="click->admin-command#close"
        class="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-100 transition-colors group border-l-2 border-transparent"
      >
        <span data-controller="admin-icon" data-admin-icon-name-value="${icons[type]}" data-admin-icon-size-value="16" class="text-neutral-400 group-hover:text-neutral-600"></span>
        <div class="flex-1 min-w-0">
          <p class="text-sm text-neutral-700 group-hover:text-neutral-900 truncate">${this.escapeHtml(item.title)}</p>
          ${item.subtitle ? `<p class="text-xs text-neutral-500 truncate">${this.escapeHtml(item.subtitle)}</p>` : ""}
        </div>
      </a>
    `
  }

  // Keyboard navigation
  handleKeydown(event) {
    const items = this.itemTargets

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectNext(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectPrevious(items)
        break
      case "Enter":
        event.preventDefault()
        this.activateSelected(items)
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  // Select next item
  selectNext(items) {
    if (items.length === 0) return

    // Remove current selection
    if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
      items[this.selectedIndex].classList.remove("bg-neutral-100", "border-primary-500")
      items[this.selectedIndex].classList.add("border-transparent")
    }

    // Move to next item
    this.selectedIndex = (this.selectedIndex + 1) % items.length

    // Apply selection
    items[this.selectedIndex].classList.add("bg-neutral-100", "border-primary-500")
    items[this.selectedIndex].classList.remove("border-transparent")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  // Select previous item
  selectPrevious(items) {
    if (items.length === 0) return

    // Remove current selection
    if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
      items[this.selectedIndex].classList.remove("bg-neutral-100", "border-primary-500")
      items[this.selectedIndex].classList.add("border-transparent")
    }

    // Move to previous item
    this.selectedIndex = this.selectedIndex <= 0 ? items.length - 1 : this.selectedIndex - 1

    // Apply selection
    items[this.selectedIndex].classList.add("bg-neutral-100", "border-primary-500")
    items[this.selectedIndex].classList.remove("border-transparent")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  // Activate selected item
  activateSelected(items) {
    if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
      items[this.selectedIndex].click()
    }
  }

  // State management
  showLoading() {
    this.hideAllStates()
    this.loadingTarget.classList.remove("hidden")
  }

  showEmpty() {
    this.hideAllStates()
    this.emptyTarget.classList.remove("hidden")

    // Show recent searches if available
    if (this.hasRecentSearches()) {
      this.recentContainerTarget.classList.remove("hidden")
    }
  }

  showNoResults() {
    this.hideAllStates()
    this.noResultsTarget.classList.remove("hidden")
  }

  hideAllStates() {
    this.loadingTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
    this.noResultsTarget.classList.add("hidden")
    this.searchResultsTarget.classList.add("hidden")
  }

  // Recent searches management
  saveRecentSearch(query) {
    const recent = this.getRecentSearches()

    // Remove if already exists
    const filtered = recent.filter(q => q !== query)

    // Add to beginning
    filtered.unshift(query)

    // Keep only last 5
    const limited = filtered.slice(0, 5)

    // Save to localStorage
    localStorage.setItem("admin_recent_searches", JSON.stringify(limited))

    // Update UI
    this.loadRecentSearches()
  }

  loadRecentSearches() {
    const recent = this.getRecentSearches()

    if (recent.length === 0) {
      this.recentContainerTarget.classList.add("hidden")
      return
    }

    this.recentContainerTarget.classList.remove("hidden")
    this.recentListTarget.innerHTML = recent.map(query => `
      <button
        type="button"
        data-action="click->admin-command#selectRecentSearch"
        data-query="${this.escapeHtml(query)}"
        class="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-neutral-100 transition-colors group w-full text-left"
      >
        <span data-controller="admin-icon" data-admin-icon-name-value="clock" data-admin-icon-size-value="14" class="text-neutral-400"></span>
        <span class="text-sm text-neutral-600">${this.escapeHtml(query)}</span>
      </button>
    `).join("")
  }

  selectRecentSearch(event) {
    const query = event.currentTarget.dataset.query
    this.inputTarget.value = query
    this.performSearch(query)
  }

  getRecentSearches() {
    try {
      const stored = localStorage.getItem("admin_recent_searches")
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error("Failed to load recent searches:", error)
      return []
    }
  }

  hasRecentSearches() {
    return this.getRecentSearches().length > 0
  }

  // Utility: Escape HTML
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
